import { Home, TrendingUp, ArrowDownLeft, ArrowUpRight, Grid, Layers, Receipt, CalendarDays, ShieldCheck, User, HelpCircle, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

const PRIMARY = [
  { id: 'dashboard', label: 'Home', icon: Home },
  { id: 'trades', label: 'Trades', icon: TrendingUp },
  { id: 'deposit', label: 'Deposit', icon: ArrowDownLeft },
  { id: 'withdraw', label: 'Withdraw', icon: ArrowUpRight },
  { id: 'more', label: 'More', icon: Grid },
];

const MORE_ITEMS = [
  { id: 'plans', label: 'ETF Plans', icon: Layers, desc: 'High-yield managed portfolios' },
  { id: 'transactions', label: 'Transactions', icon: Receipt, desc: 'Deposit & withdrawal history' },
  { id: 'calendar', label: 'P&L Calendar', icon: CalendarDays, desc: 'Daily performance ledger' },
  { id: 'kyc', label: 'KYC Verification', icon: ShieldCheck, desc: 'ID, documents & liveness' },
  { id: 'profile', label: 'Profile & Banking', icon: User, desc: 'Account details & bank setup' },
  { id: 'support', label: 'Help & Support', icon: HelpCircle, desc: 'FAQs and support tickets' },
];

export const GrowwBottomNav = ({ activeTab, onTabChange, moreOpen, setMoreOpen }) => {
  const isMoreActive = MORE_ITEMS.some((m) => m.id === activeTab);

  const handleClick = (id) => {
    if (id === 'more') {
      setMoreOpen(true);
      return;
    }
    setMoreOpen(false);
    onTabChange(id);
  };

  return (
    <>
      {/* Bottom nav — mobile only */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-brand-border"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="groww-bottom-nav"
      >
        <div className="grid grid-cols-5 h-16">
          {PRIMARY.map((item) => {
            const Icon = item.icon;
            const isActive = item.id === 'more' ? (moreOpen || isMoreActive) : (activeTab === item.id && !moreOpen);
            return (
              <button
                key={item.id}
                onClick={() => handleClick(item.id)}
                className={`flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95 ${isActive ? 'text-brand-primary' : 'text-brand-text-secondary'}`}
                data-testid={`mobile-nav-${item.id}`}
              >
                <div className={`w-10 h-7 rounded-full flex items-center justify-center transition-colors ${isActive ? 'bg-brand-primary-subtle' : ''}`}>
                  <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
                </div>
                <span className={`text-[10px] ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* More sheet — mobile */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] p-0" data-testid="mobile-more-sheet">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-brand-border">
            <SheetTitle className="flex items-center justify-between text-base font-heading font-bold">
              <span>More Options</span>
              <button onClick={() => setMoreOpen(false)} className="w-8 h-8 rounded-full bg-brand-bg flex items-center justify-center text-brand-text-secondary" aria-label="Close">
                <X size={16} />
              </button>
            </SheetTitle>
          </SheetHeader>
          <div className="p-3 overflow-y-auto">
            {MORE_ITEMS.map((m) => {
              const Icon = m.icon;
              const isActive = activeTab === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => { onTabChange(m.id); setMoreOpen(false); }}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl mb-1 transition-all active:scale-[0.98] ${isActive ? 'bg-brand-primary-subtle' : 'hover:bg-brand-bg'}`}
                  data-testid={`sheet-nav-${m.id}`}
                >
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center ${isActive ? 'bg-brand-primary text-white' : 'bg-brand-bg text-brand-primary'}`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-brand-text-primary">{m.label}</p>
                    <p className="text-[11px] text-brand-text-secondary">{m.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
